import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AccessGateProvider, useAccessGate } from "./contexts/AccessGateContext";
import { CookieConsent } from "./components/CookieConsent";
import { ComingSoon } from "./components/ComingSoon";
import { ScrollToTop } from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import Index from "./pages/Index";
import MyGifts from "./pages/MyGifts";
import UnifiedLogin from "./pages/UnifiedLogin";
import OAuthSuccess from "./pages/OAuthSuccess";
import ResetPassword from "./pages/ResetPassword";
import ProfessionalDashboard from "./pages/ProfessionalDashboard";
import ProfessionalOnboarding from "./pages/ProfessionalOnboarding";
import AdminDashboard from "./pages/AdminDashboard";
import Pricing from "./pages/Pricing";
import AcceptInvite from "./pages/AcceptInvite";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import RefundPolicy from "./pages/RefundPolicy";
import Demo from "./pages/Demo";
import NotFound from "./pages/NotFound";
import EventDiscussion from "./pages/EventDiscussion";
import PurchaseDiscussion from "./pages/PurchaseDiscussion";
import EventManagement from "./pages/EventManagement";
import JoinEvent from "./pages/JoinEvent";
import MyActivity from "./pages/MyActivity";
import FAQ from "./pages/FAQ";
import ProfessionalFAQ from "./pages/ProfessionalFAQ";

const queryClient = new QueryClient();

function AccessGatedLayout() {
  const { hasAccess, grantAccess } = useAccessGate();

  if (!hasAccess) {
    return <ComingSoon onAccessGranted={grantAccess} />;
  }

  return (
    <>
      <ScrollToTop />
      <Outlet />
      <CookieConsent />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <AccessGatedLayout />,
    children: [
      { path: "/", element: <Index /> },
      { path: "/oauth-success", element: <OAuthSuccess /> },
      { path: "/my-purchases", element: <MyGifts /> },
      { path: "/my-activity", element: <MyActivity /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/login", element: <UnifiedLogin /> },
      { path: "/merchant-login", element: <Navigate to="/login" replace /> },
      { path: "/customer-login", element: <Navigate to="/login?type=customer" replace /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { 
        path: "/professional/onboarding", 
        element: <ProtectedRoute><ProfessionalOnboarding /></ProtectedRoute>
      },
      { 
        path: "/professional-dashboard", 
        element: <ProtectedRoute><ProfessionalDashboard /></ProtectedRoute>
      },
      { 
        path: "/admin", 
        element: <ProtectedRoute><AdminDashboard /></ProtectedRoute>
      },
      { path: "/events/:reminderId", element: <EventDiscussion /> },
      { path: "/purchases/:id", element: <PurchaseDiscussion /> },
      { path: "/join/:type/:token", element: <JoinEvent /> },
      { path: "/event-management", element: <EventManagement /> },
      { path: "/accept-invite", element: <AcceptInvite /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/privacy", element: <Navigate to="/privacy-policy" replace /> },
      { path: "/terms-of-service", element: <TermsOfService /> },
      { path: "/terms", element: <Navigate to="/terms-of-service" replace /> },
      { path: "/contact", element: <Contact /> },
      { path: "/refund-policy", element: <RefundPolicy /> },
      { path: "/refund", element: <Navigate to="/refund-policy" replace /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/professional-faq", element: <ProfessionalFAQ /> },
      { path: "/demo", element: <Demo /> },
      // Redirect old merchant routes
      { path: "/merchant-dashboard", element: <Navigate to="/professional-dashboard" replace /> },
      { path: "/merchant", element: <Navigate to="/professional-dashboard" replace /> },
      { path: "/shakatah-dashboard", element: <Navigate to="/professional-dashboard" replace /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AccessGateProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RouterProvider router={router} />
          </TooltipProvider>
        </AccessGateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
